// src/services/stockMasterDataService.js
// =============================================================================
// Frontend API wrapper for operational Stock master data
// =============================================================================
//
// PURPOSE
// -------
// Stock operations need two small dictionaries that were initially seeded with
// SQL during development:
// - stock_locations      = logical places where products can physically stay,
// - units_of_measure     = controlled units used by products and stock.
//
// These are now exposed to users through the Stock menu. The backend remains
// authoritative for validation, but keeping all calls here keeps pages simple
// and avoids spreading endpoint URLs through UI code.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[StockMasterDataService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

function assertCode(context, code) {
  if (code === undefined || code === null || `${code}`.trim() === "") {
    const e = new Error(`${context}: code is required.`);
    e.code = "CODE_MISSING";
    throw e;
  }
}

async function getList(path, params = {}) {
  try {
    const res = await apiClient.get(path, { withCredentials: true, params });
    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError(`GET ${path}`, err);
  }
}

async function post(path, payload) {
  try {
    const res = await apiClient.post(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`POST ${path}`, err);
  }
}

async function put(path, payload) {
  try {
    const res = await apiClient.put(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PUT ${path}`, err);
  }
}

async function patch(path, payload) {
  try {
    const res = await apiClient.patch(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH ${path}`, err);
  }
}

// Stock locations -----------------------------------------------------------

export const listStockLocations = (params = {}) =>
  getList("/stock/locations", params);

export const createStockLocation = (payload) =>
  post("/stock/locations", payload);

export const updateStockLocation = (id, payload) => {
  assertId("updateStockLocation", id);
  return put(`/stock/locations/${id}`, payload);
};

export const setStockLocationActive = (id, isActive) => {
  assertId("setStockLocationActive", id);
  return put(`/stock/locations/${id}`, { is_active: Boolean(isActive) });
};

// Units of measure ----------------------------------------------------------
// The existing read endpoint belongs to the Catalog API because products also
// use the same dictionary. The UI entry lives under Stock because daily stock
// users need to maintain units for inventory operations.

export const listUnitsOfMeasure = (params = {}) =>
  getList("/catalog/units-of-measure", params);

export const createUnitOfMeasure = (payload) =>
  post("/catalog/units-of-measure", payload);

export const updateUnitOfMeasure = (code, payload) => {
  assertCode("updateUnitOfMeasure", code);
  return put(`/catalog/units-of-measure/${encodeURIComponent(code)}`, payload);
};

export const setUnitOfMeasureActive = (code, isActive) => {
  assertCode("setUnitOfMeasureActive", code);
  return patch(`/catalog/units-of-measure/${encodeURIComponent(code)}/active`, {
    is_active: Boolean(isActive),
  });
};

export default {
  listStockLocations,
  createStockLocation,
  updateStockLocation,
  setStockLocationActive,
  listUnitsOfMeasure,
  createUnitOfMeasure,
  updateUnitOfMeasure,
  setUnitOfMeasureActive,
};
